import { DefaultButton, Dialog, DialogType, PrimaryButton, Stack, Text, TextField } from '@fluentui/react';
import React, { useState } from 'react';
import IvicosStrings from 'kits/language/stringKit';
import { STATUS_COLORS } from 'shared-state/identity/types';

export interface ICustomAtWorkMessageModalProps {
    isOpen: boolean;
    onConfirm: (message: string) => void;
    onDismiss: () => void;
}

const MAX_CHARACTERS = 72;

/**
 * Modal for creating a custom at-work message (location or reason).
 * Displays character limit and provides confirmation.
 */
const CustomAtWorkMessageModal: React.FC<ICustomAtWorkMessageModalProps> = ({ isOpen, onConfirm, onDismiss }) => {
    const [customMessage, setCustomMessage] = useState('');
    const characterCount = customMessage.length;
    const remainingCharacters = MAX_CHARACTERS - characterCount;
    const isValid = customMessage.trim().length > 0 && characterCount <= MAX_CHARACTERS;

    const handleConfirm = () => {
        if (isValid) {
            onConfirm(customMessage);
            setCustomMessage('');
        }
    };

    const handleDismiss = () => {
        setCustomMessage('');
        onDismiss();
    };

    const handleTextChange = (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string | undefined) => {
        const value = newValue || '';
        if (value.length <= MAX_CHARACTERS) {
            setCustomMessage(value);
        }
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={handleDismiss}
            dialogContentProps={{
                type: DialogType.close,
                title: IvicosStrings.atWorkModalTitle,
                closeButtonAriaLabel: IvicosStrings.awayModalCloseAriaLabel
            }}
            modalProps={{
                isBlocking: true,
                styles: {
                    main: {
                        maxWidth: '500px'
                    }
                }
            }}
        >
            <Stack tokens={{ childrenGap: 16 }}>
                <Text variant="small" style={{ color: '#666' }}>
                    {IvicosStrings.atWorkModalDescription}
                </Text>

                <TextField
                    placeholder={IvicosStrings.atWorkMessagePlaceholder}
                    value={customMessage}
                    onChange={handleTextChange}
                    autoFocus
                    multiline
                    rows={4}
                    styles={{
                        root: { marginBottom: 0 },
                        fieldGroup: {
                            borderRadius: 4
                        }
                    }}
                />

                {/* Character counter */}
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <Text
                        variant="small"
                        style={{
                            color: remainingCharacters <= 10 ? STATUS_COLORS.busy : '#666',
                            fontSize: 12
                        }}
                    >
                        {characterCount}/{MAX_CHARACTERS} {IvicosStrings.awayCharactersLabel}
                    </Text>
                    {remainingCharacters <= 10 && remainingCharacters > 0 && (
                        <Text variant="small" style={{ color: STATUS_COLORS.busy, fontSize: 12 }}>
                            {remainingCharacters} {IvicosStrings.awayCharactersRemaining}
                        </Text>
                    )}
                </Stack>

                {/* Buttons */}
                <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end">
                    <DefaultButton text={IvicosStrings.awayModalCancel} onClick={handleDismiss} />
                    <PrimaryButton text={IvicosStrings.awayModalConfirm} onClick={handleConfirm} disabled={!isValid} />
                </Stack>
            </Stack>
        </Dialog>
    );
};

export default CustomAtWorkMessageModal;
